import { removeStoreCodeFromRoute, currentStoreView, localizedDispatcherRouteName } from '@vue-storefront/core/lib/multistore'
import { Payload } from '../vsf-mapping-fallback/types/Payload'
import { SearchQuery } from 'storefront-query-builder'
import { AsyncDataLoader } from '@vue-storefront/core/lib/async-data-loader'
import { formatProductLink } from '@vue-storefront/core/modules/url/helpers'
import { transformProductUrl } from '@vue-storefront/core/modules/url/helpers/transformUrl'
import { isServer } from '@vue-storefront/core/helpers'
import { router } from '@vue-storefront/core/app'
import type { Category } from '@vue-storefront/core/modules/catalog-next/types/Category'

function appendChildSkuQuery (url: string, childSku?: string): string {
  if (!childSku || typeof url !== 'string') {
    return url
  }
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}childSku=${encodeURIComponent(childSku)}`
}

async function findSingleProductForCategory ({ dispatch }, category: Category) {
  const productQuery = new SearchQuery()
  productQuery.applyFilter({
    key: 'category_ids',
    value: { in: category.id }
  })

  const { items = [] } = await dispatch('product/findProducts', {
    query: productQuery,
    options: {
      separateSelectedVariant: true
    }
  }, { root: true })

  const product = items[0]
  if (!product) {
    return null
  }

  const parentSku = product.parentSku || product.sku
  return { ...product, parentSku }
}

async function resolveSingleProductCategory ({ dispatch }, category, params: Payload['params']) {
  const routeProduct = await findSingleProductForCategory({ dispatch }, category)
  if (!routeProduct) {
    return null
  }

  if (isServer) {
    let redirectUrl = formatProductLink(routeProduct, currentStoreView().storeCode) as string
    redirectUrl = appendChildSkuQuery(redirectUrl, params?.childSku)

    AsyncDataLoader.push({
      execute: async ({ context }) => {
        if (context) {
          context.server.response.redirect(301, redirectUrl)
        }
      }
    })
    return undefined
  }

  return transformProductUrl(routeProduct, params || {})
}

/**
 * When a category URL was wrongly cached as a category route (e.g. missing product_count),
 * redirect to the sole product instead of rendering the category page.
 */
export async function redirectSingleProductCategory (store, category: Category, { context } = {}) {
  if (!category?.id || category.product_count !== 1) {
    return false
  }

  const routeProduct = await findSingleProductForCategory({ dispatch: store.dispatch }, category)
  if (!routeProduct) {
    return false
  }

  const link = formatProductLink(routeProduct, currentStoreView().storeCode)

  if (isServer && context?.server?.response) {
    const redirectUrl = typeof link === 'string' ? link : (link as { path?: string }).path
    if (redirectUrl) {
      context.server.response.redirect(301, redirectUrl)
      return true
    }
    return false
  }

  if (!isServer) {
    await router.replace(link as string)
    return true
  }

  return false
}

export const forCategory = async ({ dispatch }, { url, params }: Payload) => {
  const { storeCode, appendStoreCode } = currentStoreView()
  url = (removeStoreCodeFromRoute(url.startsWith('/') ? url.slice(1) : url) as string)
  url = url.replace(/\/$/, '')
  try {
    const category = await dispatch('category/single', { key: 'url_path', value: url }, { root: true })
    if (category !== null) {
      if (category.product_count === 1) {
        return resolveSingleProductCategory({ dispatch }, category, params)
      }
      return {
        name: localizedDispatcherRouteName('category', storeCode, appendStoreCode),
        params: {
          slug: category.slug
        }
      }
    }
  } catch {
    return undefined
  }
}

export const forCms = async ({ dispatch }, { url, params }: Payload) => {
  const slug = String(url || '').replace(/^\/+/, '')

  let cmsPage = null
  try {
    cmsPage = await dispatch('cmsPage/single', {
      value: slug,
      setCurrent: true
    }, { root: true })
  } catch (e) {}

  if (cmsPage) {
    return {
      name: 'cms-page',
      params: {
        slug
      }
    }
  }
}
